/**
 * Cliente do gateway do WhatsApp Web da extensão (protocolo v1; especificação em
 * docs/whatsapp-gateway.md da extensão, decisões 414 a 423).
 *
 * O painel abre uma porta `externally_connectable` com a extensão ("ml-whatsapp"). A extensão
 * repassa os pedidos à aba do WhatsApp Web que a pessoa deixa aberta e logada no mesmo Chrome.
 * Nada passa por servidor. O service worker da extensão dorme: quando a porta cai, a próxima
 * chamada reconecta e manda o `hello` de novo.
 */
import { getBrand } from '../config/brand';

const PROTOCOL = 'ml-whatsapp';
const PORT_NAME = 'ml-whatsapp';
/** A capacidade que a extensão anuncia quando tem o gateway. */
export const WHATSAPP_GATEWAY_CAPABILITY = 'whatsapp-gateway-v1';
/** O gateway corta o `getContactInfo` em 20 pessoas por pedido. */
export const CONTACT_INFO_LIMIT = 20;

export type WhatsappEngine = 'absent' | 'loading' | 'ready' | 'unsupported';
export type WhatsappSession = 'unknown' | 'loading' | 'qr' | 'connected' | 'conflict' | 'offline';

export interface WhatsappState {
  enabled: boolean;
  permission: 'granted' | 'missing';
  tab: { open: boolean; count: number };
  engine: WhatsappEngine;
  session: WhatsappSession;
  me: { id: string; phone: string; lid: string; name: string; aliases: string[] } | null;
  waVersion: string | null;
}

export interface WhatsappGroup {
  id: string;
  name: string;
  description: string;
  participantsCount: number;
  isAdmin: boolean;
  createdAt: number;
  timestamp: number;
}

export type WhatsappBusinessType = 'smb' | 'enterprise' | null;

export interface WhatsappParticipant {
  id: string;
  phone: string;
  lid: string;
  name: string;
  pushname: string;
  verifiedName: string;
  isBusiness: boolean;
  businessType: WhatsappBusinessType;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isMe: boolean;
  isMyContact: boolean;
  statusText: string;
}

export interface WhatsappBusinessProfile {
  description: string;
  email: string;
  websites: string[];
  address: string;
  categories: string[];
  /** Os mesmos ramos com o código, que não muda com o idioma (extensão 7.2.0.0 desde 03/11/2026). */
  categoryList?: Array<{ id: string; name: string }>;
}

export interface WhatsappContactInfo {
  id: string;
  phone: string;
  lid: string;
  name: string;
  pushname: string;
  verifiedName: string;
  isBusiness: boolean;
  businessType: WhatsappBusinessType;
  isMyContact: boolean;
  statusText: string;
  about: string | null;
  business: WhatsappBusinessProfile | null;
  avatar: string | null;
  partial: boolean;
}

/** Um contato que o WhatsApp da conta conhece (getContacts): uma entrada por pessoa. */
export interface WhatsappContact {
  id: string;
  phone: string;
  lid: string;
  name: string;
  pushname: string;
  verifiedName: string;
  isBusiness: boolean;
  businessType: WhatsappBusinessType;
  isMyContact: boolean;
  statusText: string;
}

/** Uma conversa, sem as mensagens (a Rede usa só a data da última). */
export interface WhatsappChat {
  id: string;
  kind: 'user' | 'group' | 'other';
  name: string;
  phone: string;
  lid: string;
  timestamp: number;
}

/** Resultado da consulta em lote (lookupContacts): quem é empresa, com o nome comercial. */
export interface WhatsappLookup {
  id: string;
  phone: string;
  lid: string;
  /** null: o WhatsApp não respondeu àquele lote. */
  isBusiness: boolean | null;
  verifiedName: string;
}

/** O gateway corta a consulta em lote em 500 contatos por pedido. */
export const LOOKUP_LIMIT = 500;

export type WhatsappErrorCode =
  | 'INVALID_REQUEST'
  | 'UNKNOWN_COMMAND'
  | 'NOT_ENABLED'
  | 'PERMISSION_MISSING'
  | 'TAB_NOT_OPEN'
  | 'ENGINE_NOT_READY'
  | 'SESSION_NOT_CONNECTED'
  | 'ACCOUNT_CHANGED'
  | 'INVALID_RECIPIENT'
  | 'NOT_FOUND'
  | 'SEND_UNCONFIRMED'
  | 'TIMEOUT'
  | 'TAB_CLOSED'
  | 'FAILED'
  /** Do próprio painel: a extensão não respondeu ou não aceita a conexão. */
  | 'NO_EXTENSION';

export class WhatsappGatewayError extends Error {
  readonly code: WhatsappErrorCode;
  constructor(code: WhatsappErrorCode, message: string) {
    super(message);
    this.name = 'WhatsappGatewayError';
    this.code = code;
  }
}

interface HelloResult {
  protocol: number;
  version: string;
  commands: string[];
  state: WhatsappState;
}

// O painel não tem os tipos do Chrome: só o pedaço da API de portas que o gateway usa.
interface RuntimePort {
  postMessage(message: unknown): void;
  disconnect(): void;
  onMessage: { addListener(fn: (message: unknown) => void): void };
  onDisconnect: { addListener(fn: () => void): void };
}
interface ChromeRuntime {
  connect(extensionId: string, info: { name: string }): RuntimePort;
  readonly lastError?: { message?: string };
}

function chromeRuntime(): ChromeRuntime | null {
  const chromeApi = (globalThis as { chrome?: { runtime?: ChromeRuntime } }).chrome;
  return chromeApi?.runtime && typeof chromeApi.runtime.connect === 'function' ? chromeApi.runtime : null;
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (reason: WhatsappGatewayError) => void;
  timer: ReturnType<typeof setTimeout>;
}

type StateListener = (state: WhatsappState) => void;

/** Prazo do painel: um pouco acima do prazo do comando no service worker. */
const TIMEOUTS: Record<string, number> = {
  hello: 8000,
  getState: 8000,
  openWhatsApp: 15000,
  openChat: 35000,
  getGroups: 65000,
  getGroupParticipants: 65000,
  getContacts: 65000,
  getChats: 35000,
  lookupContacts: 65000,
  getContactInfo: 65000,
};

export class WhatsappGateway {
  private port: RuntimePort | null = null;
  private connecting: Promise<void> | null = null;
  private pending = new Map<string, Pending>();
  private listeners = new Set<StateListener>();
  private lastSeq = 0;
  private nextId = 1;
  private current: WhatsappState | null = null;

  constructor(private readonly extensionId: string) {}

  /** O último estado conhecido (do `hello`, de uma resposta ou do evento `state.changed`). */
  get state(): WhatsappState | null {
    return this.current;
  }

  /** Avisa a cada mudança de estado. Devolve a função que para de ouvir. */
  onState(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async getState(): Promise<WhatsappState> {
    // A resposta com `state` já atualiza o estado (onMessage).
    return (await this.call<{ state: WhatsappState }>('getState')).state;
  }

  openWhatsApp(focus = true): Promise<{ tabId: number; created: boolean }> {
    return this.call('openWhatsApp', { focus });
  }

  async getGroups(): Promise<WhatsappGroup[]> {
    return (await this.call<{ groups: WhatsappGroup[] }>('getGroups')).groups;
  }

  async getGroupParticipants(groupId: string): Promise<WhatsappParticipant[]> {
    return (await this.call<{ participants: WhatsappParticipant[] }>('getGroupParticipants', { groupId }))
      .participants;
  }

  async getContacts(): Promise<WhatsappContact[]> {
    return (await this.call<{ contacts: WhatsappContact[] }>('getContacts')).contacts;
  }

  /** Todas as conversas, da mais recente para a mais antiga, em páginas de 200. */
  async getAllChats(): Promise<WhatsappChat[]> {
    const all: WhatsappChat[] = [];
    let before: number | undefined;
    for (let page = 0; page < 50; page += 1) {
      const result = await this.call<{ chats: WhatsappChat[]; hasMore: boolean }>('getChats', {
        limit: 200,
        ...(before ? { before } : {}),
      });
      all.push(...result.chats);
      const last = result.chats[result.chats.length - 1];
      if (!result.hasMore || !last || !last.timestamp) break;
      before = last.timestamp;
    }
    return all;
  }

  async lookupContacts(ids: string[]): Promise<WhatsappLookup[]> {
    if (!ids.length) return [];
    return (await this.call<{ results: WhatsappLookup[] }>('lookupContacts', { ids: ids.slice(0, LOOKUP_LIMIT) })).results;
  }

  /**
   * Recado, perfil comercial e foto. `business: true` avisa que são empresas (a Rede sabe pela
   * consulta em lote): a extensão 7.2 lê o perfil comercial mesmo sem a marca de empresa no
   * WhatsApp Web. As versões anteriores ignoram o aviso.
   */
  async getContactInfo(ids: string[], options: { business?: boolean } = {}): Promise<WhatsappContactInfo[]> {
    if (!ids.length) return [];
    const params = { ids: ids.slice(0, CONTACT_INFO_LIMIT), ...(options.business ? { business: true } : {}) };
    return (await this.call<{ contacts: WhatsappContactInfo[] }>('getContactInfo', params)).contacts;
  }

  /**
   * Abre a conversa. Com `text`, deixa o texto escrito na caixa da conversa, sem enviar
   * (`drafted: true`); a conversa que já tem um rascunho não é tocada. Extensões anteriores
   * ignoram o `text` e não mandam `drafted`.
   */
  openChat(
    phoneOrChatId: string,
    options: { text?: string } = {},
  ): Promise<{ chatId: string; opened: boolean; drafted?: boolean }> {
    const params: Record<string, unknown> = /@/.test(phoneOrChatId) ? { chatId: phoneOrChatId } : { phone: phoneOrChatId };
    if (options.text) params['text'] = options.text;
    return this.call('openChat', params);
  }

  /** Um pedido ao gateway. Conecta (ou reconecta) antes, se preciso. */
  async call<T>(command: string, params: Record<string, unknown> = {}): Promise<T> {
    await this.ensureConnected();
    return this.send<T>(command, params);
  }

  disconnect(): void {
    const port = this.port;
    this.port = null;
    try {
      port?.disconnect();
    } catch {
      /* a porta já caiu */
    }
    this.failAll(new WhatsappGatewayError('NO_EXTENSION', 'Disconnected.'));
  }

  private ensureConnected(): Promise<void> {
    if (this.port) return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.connecting = this.open().finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  private async open(): Promise<void> {
    const runtime = chromeRuntime();
    if (!runtime) throw new WhatsappGatewayError('NO_EXTENSION', 'chrome.runtime.connect is not available.');
    let port: RuntimePort;
    try {
      port = runtime.connect(this.extensionId, { name: PORT_NAME });
    } catch (error) {
      throw new WhatsappGatewayError('NO_EXTENSION', String((error as Error)?.message || error));
    }
    this.port = port;
    port.onMessage.addListener((message) => this.onMessage(message));
    port.onDisconnect.addListener(() => {
      // Sem extensão (ou origem recusada), o Chrome derruba a porta na hora e preenche lastError.
      void runtime.lastError;
      if (this.port === port) this.port = null;
      this.failAll(new WhatsappGatewayError('NO_EXTENSION', 'The extension closed the connection.'));
    });
    // O hello devolve o estado, que onMessage repassa; com `lastSeq`, o gateway reenvia os
    // eventos perdidos enquanto a porta esteve fechada.
    await this.send<HelloResult>('hello', { lastSeq: this.lastSeq });
  }

  private send<T>(command: string, params: Record<string, unknown>): Promise<T> {
    const port = this.port;
    if (!port) return Promise.reject(new WhatsappGatewayError('NO_EXTENSION', 'Not connected.'));
    const id = `r${this.nextId++}`;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new WhatsappGatewayError('TIMEOUT', `${command} did not answer in time.`));
      }, TIMEOUTS[command] ?? 35000);
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject, timer });
      try {
        port.postMessage({ protocol: PROTOCOL, type: 'request', id, command, params });
      } catch (error) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(new WhatsappGatewayError('NO_EXTENSION', String((error as Error)?.message || error)));
      }
    });
  }

  private onMessage(raw: unknown): void {
    if (!raw || typeof raw !== 'object') return;
    const message = raw as Record<string, unknown>;
    if (message.protocol !== PROTOCOL) return;
    if (message.type === 'response' && typeof message.id === 'string') {
      const pending = this.pending.get(message.id);
      if (!pending) return;
      this.pending.delete(message.id);
      clearTimeout(pending.timer);
      if (message.ok) {
        const result = message.result as Record<string, unknown> | undefined;
        if (result && typeof result === 'object' && 'state' in result && result.state) {
          this.setState(result.state as WhatsappState);
        }
        pending.resolve(result);
      } else {
        const error = (message.error || {}) as { code?: WhatsappErrorCode; message?: string };
        pending.reject(new WhatsappGatewayError(error.code || 'FAILED', error.message || 'Gateway error.'));
      }
      return;
    }
    if (message.type === 'event') {
      if (typeof message.seq === 'number') this.lastSeq = Math.max(this.lastSeq, message.seq);
      const data = (message.data || {}) as { state?: WhatsappState };
      if (message.event === 'state.changed' && data.state) this.setState(data.state);
    }
  }

  private setState(state: WhatsappState): void {
    this.current = state;
    this.listeners.forEach((listener) => listener(state));
  }

  private failAll(error: WhatsappGatewayError): void {
    this.pending.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(error);
    });
    this.pending.clear();
  }
}

/** O que as telas usam do gateway (o de verdade, ou o de demonstração das prévias locais). */
export type WhatsappGatewayApi = Pick<
  WhatsappGateway,
  | 'state'
  | 'onState'
  | 'getState'
  | 'openWhatsApp'
  | 'getGroups'
  | 'getGroupParticipants'
  | 'getContactInfo'
  | 'getContacts'
  | 'getAllChats'
  | 'lookupContacts'
  | 'openChat'
>;

let shared: WhatsappGateway | null = null;

/** O gateway da marca atual, um por aba do painel. */
export function getWhatsappGateway(): WhatsappGateway {
  if (!shared) shared = new WhatsappGateway(getBrand().extensionId);
  return shared;
}

/** O endereço da página de ativação da extensão (cartão "Conectar" ou switch). */
export function whatsappPromptUrl(mode: 'card' | 'toggle' = 'card'): string {
  return `chrome-extension://${getBrand().extensionId}/html/whatsappPrompt.html${mode === 'toggle' ? '?mode=toggle' : ''}`;
}
